import { globals } from 'config/globals';
import { Core } from 'core/Core';
import { device } from 'utils/device';
import { constrain, getBestFitScale, getRangeRatio } from 'utils/math';
import { HitArea } from 'app/module/global/HitArea';

import { PixiController } from 'app/services/PixiController';
import { WorkTileImagePixi, WorkTileImageConfig } from './WorkTileImagePixi';
import { WorkTileParticlePixi, WorkTileParticleConfig } from './WorkTileParticlePixi';

import { ProjectConfig } from 'app/model/ProjectConfig';
import { ProjectParticleConfig } from 'app/model/ProjectParticleConfig';

import { MouseController } from 'app/chrome/mouse/MouseController';

import { Inertia } from 'utils/motion/Inertia';
import ProjectHeaderSizes from 'app/views/project/ProjectHeaderSizes';
import WorkCarouselSizes from 'app/views/work/WorkCarouselSizes';
import { Events } from 'core/events/Events';
import Config from 'config/settings';
import fastdom from 'fastdom';
import SiteTransitions from 'app/services/SiteTransitions';

export interface Size {
	width: number;
	height: number;
}

//Config when the project is focused and zoomed in:
export interface ZoomConfig {
	tileX: number;
	tileY: number;
	tileOffsetX?: number;
	tileOffsetY?: number;
	imageX: number;
	imageY: number;
	imageScale: number;
	maskScaleX: number;
	maskScaleY: number;
}

interface WorkSpritesheets {
	particles: PIXI.Spritesheet; //--> PNG 8
	particles32: PIXI.Spritesheet; //--> PNG 32
	backgrounds: PIXI.Spritesheet;
	backgroundsLite?: PIXI.Spritesheet;
}

export class WorkTilePixi {
	public onExploreProject: Function;

	private _mouse: MouseController;

	//PIXI JS elements
	private _$pixi: PIXI.Application;
	private _$container: PIXI.Container;
	private _$bgGraphicGuide: PIXI.Graphics;
	private _spritesheets: WorkSpritesheets;
	private _maskedimage: WorkTileImagePixi;
	private _particles: Array<WorkTileParticlePixi> = [];

	private _$copy: HTMLElement;
	private _$exploreProgress: HTMLElement;
	private _$copyTitle: HTMLElement;
	//Write-only state (assigned but never read anywhere — public so noUnusedLocals
	//doesn't flag them; candidates for removal in a runtime-change pass):
	public _titleY: number; //Last translateY applied to the copy title
	public _hitX: number; //Hit area x (px)
	public _hitY: string; //Hit area y (CSS percentage string, e.g. '69%')
	private _$copySubTitle: HTMLElement;
	private _$circlePulse: HTMLElement;

	private _copyX: number;
	private _copyY: string;
	private _copyVisible: boolean = false;
	private _backgroundAnchor: number = 0.5;

	private _$hitarea: HitArea;
	private _hitAreaClickable: boolean = false;
	private _exploreProgressRatio: number = 0;

	private _width: number = 1000;
	private _height: number = 600;
	private _innerWidth: number;
	private _innerHeight: number;
	private _maskArea: Size;

	private _progress: number = 0.0;
	private _velocity: number = 0.0;
	private _velocityRotation: number = 0.0;

	private _projectActive: boolean;
	private _hiddenForProject: boolean = false;
	private _isBlankSpacer: boolean = false;


	private _parallaxRatio: Inertia;
	private _widthMulipler: number;

	private _void: boolean = false;
	private _paused: boolean = false;
	private _debug: boolean = false;
	private _projectExitScrolling: boolean = false;


	//DEBUG
	//-------------
	private _displayBackgroundGuide: boolean = false;

	constructor(
		private _pixi: PixiController,
		private _index: number,
		private _data: ProjectConfig,
		private _$workContainer: PIXI.Container,
		parentContainer: HTMLElement
	) {
		//If Pixi is not set this is a 'blank' spacer
		if (!_pixi) {
			this._isBlankSpacer = true;
			return;
		}

		if (this._index == 3) {
			this._debug = true;
		}

		this._innerWidth = device.innerWidth;
		this._innerHeight = device.innerHeight;

		this._$pixi = _pixi.getPixi();
		this._$copy = parentContainer.querySelector(`.worktitle__copy.-index${_index}`);

		if (this._$copy) {
			this._$copyTitle = this._$copy.querySelector('.worktitle__title');
			this._$copySubTitle = this._$copy.querySelector('.worktitle__subtitle');

			this._$circlePulse = parentContainer.querySelector(`.ui_core_button_circle.-index${_index}`);
			this._$exploreProgress = this._$copy.querySelector(`.ui__explore__progress`);
		} else {
			this._void = true;
			return;
		}

		this._parallaxRatio = new Inertia(1.0, 0);

		this._mouse = Core.controller.get('Mouse') as MouseController;

		this._backgroundAnchor = this._data.anchor || this._data.anchor === 0 ? this._data.anchor : 0.5;
	}

	//Set the spritesheet refs to load backgrounds and images from
	public setSpriteSheets(
		particles: PIXI.Spritesheet,
		backgrounds: PIXI.Spritesheet,
		backgroundsLite: PIXI.Spritesheet,
		particles32: PIXI.Spritesheet
	): void {
		this._spritesheets = {
			particles: particles,
			backgrounds: backgrounds,
			backgroundsLite: backgroundsLite,
			particles32: particles32
		};
	}

	/** Set the hit area for the space over the tile */
	public setHitarea(area: HitArea): void {
		if (this._void) {
			return;
		}

		this._$hitarea = area;

		if (this._$hitarea) {
			this._$hitarea.config.onUpdate = (updateOb: any) => {
				this._maskedimage.setMouseDownScale(updateOb.ratio);
				this._exploreProgressRatio = updateOb.ratio;

				fastdom.mutate(() => {
					this._$exploreProgress.style.transform = `scaleX(${this._exploreProgressRatio})`;
				});
			};
			this._$hitarea.config.onComplete = (data) => {
				this.onExploreProject(data);
			};
		}
	}

	public get isVisible(): boolean {
		return this._$container && this._$container.visible;
	}

	public get hitAreaMouseDown(): boolean {
		return this._$hitarea && this._$hitarea.mouseIsDown;
	}

	//Create the tile
	public create(): void {
		if (!this._$workContainer) {
			console.warn('Could not run WorkTilePixi.create() because this._$workContainer is not set.');
			this._void = true;
		}

		if (this._void) {
			return;
		}

		this._createContainer();

		//Add partciles below, then image, then top partciles:
		this._createParticles(-1);
		this._createMaskedImage();
		this._createParticles(1);
		this._createParticles(-2);

		//Trigger resize to make sure everything is in place:
		this.resize();
	}

	public startExitScroll(maskStyle) {
		this._particles.forEach((p) => {
			p.startExitScroll(maskStyle);
		});

		this._maskedimage.startExitScroll(maskStyle);
		this._projectExitScrolling = true;
	}

	///Set the
	public setTimelineProgress(
		ratio: number,
		skipchecks: boolean = false,
		velocity: number = 0,
		forceUpdate: boolean = false
	): number {
		if (this._void) {
			return null;
		}

		if (this._isBlankSpacer) {
			return null;
		}

		//Exit if prratio has not changed:
		if (!skipchecks) {
			if (this._projectActive) {
				return null;
			}
		}

		this._velocity = velocity;
		this._progress = ratio;

		//Update animation progress and set container visible:
		this._animate(this._progress, forceUpdate);
		this._setContainerVisible(this._progress);
		this._setCopyVisible(this._progress);
		this._setHitAreaClickable();

		return this._progress;
	}

	public resize(skipChanges: boolean = false, passThrough = false): void {
		if (this._void) {
			return;
		}

		if (this._projectActive && !passThrough) {
			Math.floor((device.innerWidth - innerWidth * 0.85) / 2);
			return;
		}

		if (this._isBlankSpacer) {
			return;
		}

		//Set the height and width refs:
		this._innerWidth = device.innerWidth;
		this._innerHeight = device.innerHeight;

		//Size the tile to fit layout and other dependant sizes:
		this._resizeTile();
		this._resizeMaskArea(skipChanges);
		this._resizePartciles();
		this._resizeCopy();

		this._setContainerX(this._progress);
		this._setContainerY();
		this._setCopyX();

		this._setHitAreaPosition();
		this._animate(this._progress);

		this._$hitarea.resizeArea(this._width, this._height);
	}

	public hardReset() {
		if (this._void) {
			return;
		}

		this._projectActive = false;
		this._maskedimage.showForWork();
		this.resize();
	}

	public resizeMaskForHardReset() {
		if (this._void) {
			return;
		}
		this.resize(false, true);
		this._resizeMaskArea(false);
		this._maskedimage.resizeMask();
	}

	public resetToZeroScroll() {
		if (this._void) {
			return;
		}
		this.scroll(0);

		this._particles.forEach((particle) => {
			particle.resetToZeroScroll();
		});
	}

	public resizeOnProject() {
		if (this._void) {
			return;
		}

		this._resizeOnProject();
	}

	public animateToProject(): void {
		if (this._void) {
			return;
		}

		this._projectActive = true;
		this._zoomToProject();
		this._projectExitScrolling = false;
		Core.DOM.addClass(this._$copy, '-project');
	}

	public backFromProject(): void {
		if (this._void) {
			return;
		}

		this._zoomFromProject();

		SiteTransitions.addCounter(
			() => (this._projectActive = false),
			1000 * Config.fromProjectMultipler * SiteTransitions.speedScale
		);
		SiteTransitions.addCounter(
			() => Core.DOM.addClass(this._$copy, '-show'),
			500 * Config.fromProjectMultipler * SiteTransitions.speedScale
		);

		Core.DOM.removeClass(this._$copy, '-project');
	}

	/** Flag mouse/touch down for hit area */
	public mouseDown(cords: any): void {
		return;
		if (this._$hitarea) {
		}

		if (Math.abs(this._velocity) < 0.001) {
			if (this._$hitarea.checksIsIntersecting(cords)) {
				this.onExploreProject(this._$hitarea.data);
			}
		}
	}

	public onClick(cords: object = null): void {
		if (this._void) {
			return;
		}

		if (globals.lastCordsFromTouch) {
			if (!cords) {
				this._mouse.getMouseCords();
			}

			if (this._$hitarea.checksIsIntersecting(cords)) {
				this.onExploreProject(this._$hitarea.data);
			}
			return;
		}

		//Classic mouse over:
		if (this._hitAreaClickable) {
			this.onExploreProject(this._$hitarea.data);
		}
	}

	public mouseUp(): void {
		if (this._void) {
			return;
		}
		if (this._$hitarea) {
			this._$hitarea.mouseUp();
		}
	}

	/** Flag mouse move for hit area */
	public updateMouseCords(cords: any): void {
		if (this._void) {
			return;
		}
		if (this._$hitarea.mouseIsDown) {
			this._$hitarea.updateMouseCords(cords);
		}
	}

	//Reset methods -------------------------------------------------------------

	/** All transitions in never show timeline item so just need one to reset to default state: */
	public reset(): void {
		if (this._void) {
			return;
		}

		Core.DOM.removeClass(this._$copy, '-show');
	}

	//-----------------------------------------------------------------------------

	public hideForProject(index: number): void {
		if (this._void) {
			return;
		}

		this._maskedimage.hideForProject();

		gsap.TweenMax.to(this._$container, 0.2 * SiteTransitions.speedScale, {
			pixi: { alpha: 0 },
			ease: Config.linearEase,
			onComplete: () => {}
		});
	}

	/** Reset deep settings on the tile. Called after a user moves away from project using deeplink methods (NAV, logom, etc) */
	public resetForProject(): void {
		if (this._void) {
			return;
		}

		this._hiddenForProject = false;
		this._$container.visible = true;

		//HAck, animate the project to set the animation state...
		if (this._projectActive) {
			this._zoomFromProject();
		}
		this._projectActive = false;
	}

	public showFromProject(index: number): void {
		if (this._void) {
			return;
		}

		this._hiddenForProject = false;

		this._maskedimage.showForWork();

		SiteTransitions.tweens.otherWorkTiles[index] = gsap.TweenMax.to(
			this._$container,
			0.7 * SiteTransitions.speedScale,
			{
				pixi: { alpha: 1 },
				delay: 0.4 * SiteTransitions.speedScale,
				ease: Config.linearEase,
				onComplete: () => {
					this._maskedimage.showForWork();
					SiteTransitions.tweens.otherWorkTiles[index] = null;
				}
			}
		);
	}

	public setVelocityRotation(rotation: number): void {
		if (this._void) {
			return;
		}

		if (this._isBlankSpacer) {
			return;
		}

		this._velocityRotation = rotation;
	}

	public exitScroll(y: number) {
		this._maskedimage.exitScroll(y);
		this._particles.forEach((particle) => {
			particle.exitScroll(y);
		});
	}

	public scroll(y: number, parallax: boolean = true): void {
		if (this._paused || this._void || !parallax || this._projectExitScrolling) {
			console.log('this._projectExitScrolling', this._projectExitScrolling);
			return;
		}

		this._particles.forEach((particle) => {
			particle.scroll(y);
		});

		this._maskedimage.scroll(y);
	}

	public caseStudyTick(y: number): void {
		if (this._void || this._paused) {
			return;
		}

		this._particles.forEach((particle) => {
			particle.caseStudyTick();
		});
	}

	public pause(): void {
		this._paused = true;
	}

	public unpause(): void {
		this._paused = false;
	}

	//private
	//-------------------------------------------------------------
	//-------------------------------------------------------------
	//-------------------------------------------------------------
	//-------------------------------------------------------------

	//The tile has a 'virtual' size. This can be seen by setting _displayBackgroundGuide to true
	private _resizeTile(): void {
		//Set width / height to limits

		let tileSize: Size = WorkCarouselSizes.tileSize();
		this._width = tileSize.width;
		this._height = tileSize.height;

		if (this._displayBackgroundGuide && this._$bgGraphicGuide) {
			this._renderBackgroundGuide();
		}
	}

	//Set the availble mask area on the tile
	//Sizes differently at breakpoints to allow for mobile / desktop
	private _resizeMaskArea(skipChanges: boolean = false): void {
		//Assume desktop size, 60% width:
		let wR = 1.0;
		let hR = 1.0;

		//On mobile then switch to a portriat layout on
		if (device.isPortraitDevice) {
			wR = 1.0;
			hR = 1.0;
		}

		this._maskArea = {
			width: this._width * wR,
			height: this._height * hR
		};

		this._maskedimage.resize(this._maskArea, { width: this._width, height: this._height }, skipChanges);
	}

	//Resize the partciles relative to the image:
	private _resizePartciles(): void {
		let imageDisplayScale: number = this._maskedimage.getDisplayScale();

		this._particles.forEach((particle) => {
			particle.resize(imageDisplayScale, this._maskedimage.computedScale);
		});
	}

	//Add the tile container to PIXI stage:
	private _createContainer(): void {
		if (!this._$workContainer) {
			console.warn('Could not create PIXI container because this._$workContainer is not set.');
			return;
		}

		this._$container = new PIXI.Container();
		this._$container.name = this._data.slug;
		this._$container.visible = false;
		this._$workContainer.addChild(this._$container);

		if (this._displayBackgroundGuide) {
			this._$bgGraphicGuide = new PIXI.Graphics();
			this._$bgGraphicGuide.name = 'BG Guide';
			this._$container.addChild(this._$bgGraphicGuide);
			this._renderBackgroundGuide();
		}
	}

	//Render the background guide to size of tile:
	private _renderBackgroundGuide(): void {
		let colours = [0x333333, 0x333333, 0x333333, 0x333333, 0x333333, 0x333333, 0x333333, 0x333333];
		this._$bgGraphicGuide.clear();
		this._$bgGraphicGuide.beginFill(colours[this._index]); // Purple
		// Draw a rectangle
		this._$bgGraphicGuide.drawRect(0, 0, this._width, this._height); // drawRect(x, y, width, height)
		this._$bgGraphicGuide.endFill();
		this._$bgGraphicGuide.alpha = 0.4;
	}

	//Add the masked image to stage. Handles it's own timeline and exit timeline
	private _createMaskedImage(): void {
		let config: WorkTileImageConfig = {};
		this._maskedimage = new WorkTileImagePixi(this._$pixi, this._$container, config, this._backgroundAnchor);
		this._maskedimage.debug = true;
		this._maskedimage.setTint(this._data.tint);

		let bg = this._spritesheets.backgrounds.textures[this._data.image];

		if (
			!bg &&
			this._spritesheets.backgroundsLite &&
			this._spritesheets.backgroundsLite.textures &&
			this._spritesheets.backgroundsLite.textures[this._data.image]
		) {
			bg = this._spritesheets.backgroundsLite.textures[this._data.image];
		}

		this._maskedimage.setTextures({
			image: bg,
			mask: this._spritesheets.particles.textures['mask.png'],
			bevel: this._spritesheets.particles.textures['bevel.png'],
			black: this._spritesheets.particles.textures['black.png'],
			glow: this._spritesheets.particles32.textures['tile-glow.png']
		});
		this._maskedimage.create();
	}

	private _reslove(value: any = null): any {
		if (value && Array.isArray(value)) {
			return value[Math.floor(Math.random() * (value.length - 0.000001))];
		}

		if (value !== undefined && value !== null) {
			return value;
		}

		return null;
	}

	/** Add the masked image to stage. Handles it's own timeline and exit timeline. Pass depth to create layering */
	private _createParticles(depth: number = 1): void {
		//Set "z" values allowing for 10 as max (although this can be passed and - numbers will work)
		let maxZ = 10;
		let maxDistance = 4;
		let maxParallaxPower = 12;

		this._data.particles.forEach((particle: ProjectParticleConfig, index) => {
			if (depth === particle.depth) {
				let scale = particle.scale || particle.scale == -0 ? particle.scale : particle.scale;
				let alpha = particle.alpha || particle.alpha == -0 ? particle.alpha : particle.alpha;

				//Set progress, parallax and inertia speed based on z if set (default to no impact. I.e 1-1 to 0)
				let distance = 0;
				let parallaxPower = 0;
				let scrollpower = 0.5;

				if (particle.z) {
					let zRatio = particle.z / maxZ;
					distance = maxDistance * zRatio;
					parallaxPower = maxParallaxPower * zRatio;
					scrollpower = 0.5 + zRatio * 0.9;
				}

				//Allow manual override on z defaults:
				if (particle.distance || particle.distance === 0) {
					distance = particle.distance;
				}

				if (particle.parallaxPower || particle.parallaxPower === 0) {
					parallaxPower = particle.parallaxPower;
				}

				let preset = this._reslove(particle.preset);
				if (preset === 'big') {
					scale = 0.7 + Math.random() * 0.7;
					alpha = 0.2 + Math.random() * 0.2;
					distance = 0.4;
				} else if (preset === 'small') {
					scale = 0.2 + Math.random() * 0.2;
					alpha = 0.7 + Math.random() * 0.2;
					distance = 2;
				}

				let x = this._normaliseX(particle.x);
				let y = this._normaliseX(particle.y);

				//Mobile scales (as not all layouts just work on mobile when moved to Portrait layout)
				let mX = particle.mobileX === 0 || particle.mobileX ? this._normaliseX(particle.mobileX) : x;
				let mY = particle.mobileY === 0 || particle.mobileY ? this._normaliseX(particle.mobileY) : y;
				let mScale = particle.mobileScale === 0 || particle.mobileScale ? particle.mobileScale : scale;

				let texture =
					this._spritesheets.particles.textures[particle.texture] ||
					this._spritesheets.particles32.textures[particle.texture];

				let config: WorkTileParticleConfig = {
					name: `${particle.texture} - Index ${index}`,
					texture: texture,
					xOrg: x,
					yOrg: y,
					standardX: x,
					standardXOrg: particle.x,
					standardY: y,
					standardYOrg: particle.y,
					mobileX: mX,
					mobileXOrg: particle.mobileX || particle.mobileX === 0 ? particle.mobileX : particle.x,
					mobileY: mY,
					mobileYOrg: particle.mobileY || particle.mobileY === 0 ? particle.mobileY : particle.y,
					projectX: particle.projectX,
					projectY: particle.projectY,
					projectScale: particle.projectScale || 1,
					mobileProjectY: particle.mobileProjectY,
					mobileProjectX: particle.mobileProjectX,
					projectMobileScale: particle.projectMobileScale || mScale,
					toProjectTimeOffset: particle.toProjectTimeOffset || 0,
					speed: distance,
					autoScale: particle.autoScale,
					autoRotate: particle.autoRotate,
					autoAlpha: particle.autoAlpha,
					retainInCaseStudy: particle.retainInCaseStudy === true,
					caseStudyHide: particle.caseStudyHide === true,
					parallaxPower: parallaxPower,
					caseStudyScrollParallax: particle.caseStudyScrollParallax || 0.2,
					caseStudyScrollRatio: scrollpower,
					midScale: scale,
					standardScale: scale,
					mobileScale: mScale,
					midAlpha: alpha,
					projectAnimationOffset: particle.projectAnimationOffset
				};

				if (particle.leftY) {
					config.leftY = particle.leftY;
				}

				if (particle.rightY) {
					config.rightY = particle.rightY;
				}

				let isMasked = depth === -2;
				let container = isMasked ? this._maskedimage.floatingMaskContainer : this._$container;

				let particlePixi = new WorkTileParticlePixi(
					this._$pixi,
					container,
					config,
					isMasked,
					depth,
					this._index + 1,
					this._particles.length
				);
				particlePixi.debug = this._debug;

				this._particles.push(particlePixi);
			}
		});
	}

	//Convert 0-1 conifg to -1 to 1
	private _normaliseX(x: number): number {
		return (x - 0.5) * 2;
	}


	public updatePixiProps(particleIndex: number, updateProps: any) {
		if (this._particles[particleIndex]) {
			this._particles[particleIndex].updateViaProps(updateProps);
		}
	}

	private _animate(progres: number, force = false): void {
		if (force || this._$container.visible) {
			//Move the cotainer left to right <--------->
			this._setContainerX(progres);
			this._setCopyX();
			this._setHitAreaPosition();

			//Update the inner elements of the tile so they can handle their own motion/scale/alpha:
			let progressContained: number = constrain(progres, 0, 1.0);
			this._maskedimage.update(
				progressContained,
				this._velocityRotation,
				false,
				this._hitAreaClickable,
				this._velocity
			);

			//Update the parallax to tween the ease (if tweening)
			this._parallaxRatio.tick();
			this._resizePartciles();
			for (var i = 0; i < this._particles.length; i++) {
				this._particles[i].update(
					progressContained,
					this._parallaxRatio.ease,
					{ height: this._height, width: this._width },
					this._maskedimage.blackOutAlpha,
					this._velocity
				);
			}
		}
	}

	private _setContainerVisible(progress: number): void {
		if (this._hiddenForProject) {
			return;
		}

		if (this._$container.visible && (progress < -0.2 || progress > 1.4)) {
			this._$container.visible = false;
		} else if (!this._$container.visible && progress >= -0.2 && progress <= 1.4) {
			this._$container.visible = true;
		}
	}

	//Create the main slide timeline
	//------------------------------------------------------------

	//Because of sizing / design issues position the main continer with pixel rather than percetages:
	//At 0.0 it will be off left but showing ident
	//At ~-0.1 it will hidden fully
	//At 0.5 there will be no transform applied and will be middle of screen
	//At 1 it will be off right but showing ident
	///At ~1.1 it will be off right hidden
	private _setContainerX(progress: number): void {
		let x = this._getContainerX(progress);
		this._$container.x = x;
	}

	private _getContainerX(progress: number): number {
		this._widthMulipler = WorkCarouselSizes.widthMuliplier();

		let offset = 0;
		let offsetRange = 1 - this._widthMulipler;
		offset = ((this._innerWidth - this._width) * offsetRange) / 2;

		return (
			getRangeRatio(
				progress,
				-1 * this._width * this._widthMulipler,
				this._innerWidth * this._widthMulipler
			) + offset
		);
	}

	private _setContainerY(): void {
		this._$container.y = this._getContainerY();
	}

	private _getContainerY(): number {
		return device.isPortraitDevice
			? device.innerHeight * 0.48 - this._height / 2
			: this._innerHeight / 2 - this._height / 2;
	}

	//Set hitara clickable state (activates rollover state and :
	private _setHitAreaClickable(): void {
		if (!this._$container.visible) {
			return;
		}

		if (globals.lastCordsFromTouch) {
			if (this._hitAreaClickable && Math.abs(this._velocity) >= 0.0001) {
				this._hitAreaClickable = false;
				if (this._$hitarea) this._$hitarea.clickable = false;
			} else if (!this._hitAreaClickable && !this._mouse.mouseIsDown() && Math.abs(this._velocity) < 0.0001) {
				this._hitAreaClickable = true;
				if (this._$hitarea) this._$hitarea.clickable = true;
			}

			return;
		}

		//Last event type setting mouse cords might be touch, if so don't process:
		if (
			this._hitAreaClickable &&
			(Math.abs(this._velocity) >= 0.0001 || !this._$hitarea.checksIsIntersecting(this._mouse.getMouseCords(), true))
		) {
			this._hitAreaClickable = false;
			this._$hitarea.clickable = false;
			this._mouse.setClickableState(false);
			this._dampendFloatingElementParallax();
			Core.DOM.removeClass(document.querySelector('body'), 'worktile-mouse-over');
		} else if (
			!this._hitAreaClickable &&
			!this._mouse.mouseIsDown() &&
			Math.abs(this._velocity) < 0.0001 &&
			this._$hitarea.checksIsIntersecting(this._mouse.getMouseCords(), true)
		) {
			this._hitAreaClickable = true;
			this._$hitarea.clickable = true;
			this._mouse.setClickableState(true);
			this._dampendFloatingElementParallax();
			Core.DOM.addClass(document.querySelector('body'), 'worktile-mouse-over');
		}
	}

	/**
	 * Remove parallax effect on mouse over
	 */
	private _dampendFloatingElementParallax(): void {
		this._particles.forEach((particle) => {
			particle.dampenParallax();
		});
	}

	//Set in the copy visible:
	private _setCopyVisible(processProgress: number): void {
		if (this._copyVisible && (processProgress < 0.41 || processProgress > 0.6)) {
			this._copyVisible = false;
			Core.DOM.removeClass(this._$copy, '-show');
			if (this._$hitarea) {
				this._$hitarea.hide();
			}

			this._maskedimage.setProjectActive(false);

			this._particles.forEach((particle) => {
				particle.setProjectActive(false);
			});
		} else if (!this._copyVisible && processProgress >= 0.41 && processProgress <= 0.6) {
			this._copyVisible = true;
			Core.DOM.addClass(this._$copy, '-show');
			Events.dispatch('work_copy_shown');
			if (this._$hitarea) {
				this._$hitarea.show();
			}

			this._maskedimage.setProjectActive(true);

			this._particles.forEach((particle) => {
				particle.setProjectActive(true);
			});
		}
	}

	private _resizeCopy(): void {
		if (this._$copy) {
			let width = device.isPortraitDevice ? this._width : this._width;

			fastdom.mutate(() => {
				this._$copy.style.width = `${Math.floor(width)}px`;
			});
		}
	}

	//Move the copy in sync with the container:
	private _setCopyX(): void {
		if (!this._$container.visible) {
			return;
		}

		this._copyX = device.isPortraitDevice ? this._$container.x + 10 : this._$container.x;
		if (this._copyX < this._innerWidth + 500 && this._copyX > -900) {
			let titleY;
			if (device.isPortraitDevice && this._innerWidth < Config.mobileBreakpoint) {
				let copyPad = ProjectHeaderSizes.heightFromMobileDesign(19, true, true);
				this._copyY = `${this._$container.y + this._height + copyPad}px`;
				titleY = -1 * (this._height + ProjectHeaderSizes.heightFromMobileDesign(51 * 1.15, true, true));
			} else {
				this._copyY = `${Math.round(this._$container.y + this._height + 45)}px`;
				titleY = -1 * (this._height + 65);
			}

			if (this._$copy) {
				fastdom.mutate(() => {
					this._$copy.style.transform = `translateX(${Math.round(this._copyX)}px) translateY(${this._copyY})`;
				});
			}

			fastdom.mutate(() => {
				this._$copyTitle.style.transform = `translateY(${Math.round(titleY)}px)`;
			});

			this._titleY = titleY;
		}
	}


	/** Set hit area position */
	private _setHitAreaPosition(): void {
		if (!this._$container.visible) {
			return;
		}

		if (!this._$hitarea) {
			return;
		}

		this._hitX = !device.isPortraitDevice
			? this._$container.x + this._width * 0.3
			: this._$container.x + this._width * 0.5;
		this._hitY = !device.isPortraitDevice ? '69%' : '59%';
		this._$hitarea.setPosition(this._$container.x, `${this._$container.y}px`, this._maskedimage.getActualScale());
	}


	private _zoomToProject(instant: boolean = false) {
		let config: ZoomConfig = this._getZoomConfig();
		let tileTime: number = instant ? 0.0 : 0.6;

		let imageSizes = this._maskedimage.getImageSizes();

		this._pixi.setProjectHeaderSize({
			width: config.imageScale * imageSizes.width,
			height: config.imageScale * imageSizes.height
		});

		this._hitAreaClickable = false;
		this._$hitarea.clickable = false;
		this._mouse.setClickableState(false);


		tileTime = Config.toProjectTime * Config.toProjectMultipler;

		//Scale up the container:
		//Save the tween to the Site Transitions where it can be cancelled if needed:
		SiteTransitions.tweens.tileContainerZoomToProject = gsap.TweenMax.to(
			this._$container,
			tileTime * SiteTransitions.speedScale,
			{
				pixi: { x: config.tileX, y: config.tileY },
				ease: Config.toProjectContainerEase,
				delay: 0,
				onComplete: () => {
					SiteTransitions.tweens.tileContainerZoomToProject = null;
				}
			}
		);

		//Tween the copy to the bottom left:
		SiteTransitions.hideWorkTileUIForProject(this._$copyTitle, this._$circlePulse, this._$copySubTitle);

		this._maskedimage.zoomToProject(instant, config);

		SiteTransitions.clearWorkTileParticleTweens();
		this._particles.forEach((particle, index) => {
			particle.zoomToProject(config.imageScale, config.tileX, config.tileY, config.imageScale, false, index);
		});

		//Save to the pixi controller the sizes of the image, they are needed to match the full screen video on the project

		this._resizePartciles();
	}

	private _resizeOnProject() {
		this._innerWidth = device.innerWidth;
		this._innerHeight = device.innerHeight;

		this.resizeMaskForHardReset();

		let tileSize: Size = WorkCarouselSizes.tileSize();
		this._width = tileSize.width;
		this._height = tileSize.height;

		let config: ZoomConfig = this._getZoomConfig();
		this._maskedimage.zoomToProject(true, config);
		gsap.TweenMax.set(this._$container, { pixi: { x: config.tileX, y: config.tileY } });

		//Do the particles:

		this._particles.forEach((particle) => {
			particle.zoomToProject(config.imageScale, config.tileX, config.tileY, config.imageScale, true);
		});
	}

	//Zoom back from the project on exit press:
	private _zoomFromProject(): void {
		this._progress = 0.5;
		this._hiddenForProject = false;

		this._setContainerX(this._progress);
		this._copyX = device.isPortraitDevice ? this._$container.x + 10 : this._$container.x;

		SiteTransitions.tweens.tileContainerZoomToProject = gsap.TweenMax.to(
			this._$container,
			Config.fromProjectTime * Config.fromProjectMultipler * SiteTransitions.speedScale,
			{
				pixi: { x: this._getContainerX(this._progress), y: this._getContainerY() },
				ease: Config.fromProjectEase,
				delay: 0,
				onComplete: () => {
					SiteTransitions.tweens.tileContainerZoomToProject = null;
					this._projectActive = false;

					//Let the work controllet know that the transitiom is complete:
					SiteTransitions.onWorkZoomBackComplete();
				}
			}
		);

		this._maskedimage.zoomFromProject();

		gsap.TweenMax.set(this._$copy, { x: this._copyX, y: this._copyY, ease: Config.linearEase });

		SiteTransitions.showWorkTileUIForProject(this._$copyTitle, this._$circlePulse, this._$copySubTitle);

		this._parallaxRatio.easeTarget = 1.0;
		this._parallaxRatio.ease = 0;
		this._parallaxRatio.easeFrameUpdate = 0.002;

		let imageSizes = { height: this._height, width: this._width };

		this._particles.forEach((particle, index) => {
			particle.zoomFromProject(this._maskedimage.getDisplayScale(), imageSizes, 1, this._parallaxRatio.ease, index);
		});
	}


	//Get the config for a project zoom:
	private _getZoomConfig(): ZoomConfig {
		let maskSizes: Size = this._maskedimage.getMaskSizes();

		maskSizes = {
			width: this._width,
			height: this._height
		};

		//width: 1268.8039225807001, height: 490.281540243538
		//{width: 1305.6000000000001, height: 504.5}

		//hhhh
		let projectHeaderSize: Size = ProjectHeaderSizes.headerSize();
		let headerYPosition: number = ProjectHeaderSizes.headerYPosition();

		return {
			tileX: this._innerWidth / 2 - this._width / 2,
			tileY: projectHeaderSize.height / 2 - this._height / 2 + headerYPosition,
			tileOffsetX: this._innerWidth / 2 - this._width / 2,
			tileOffsetY: projectHeaderSize.height / 2 - this._height / 2 + headerYPosition,

			imageX: this._width / 2,
			imageY: this._height / 2,
			//Fit image to full scale and width:
			imageScale: getBestFitScale(this._maskedimage.getImageSizes(), projectHeaderSize),
			//Mask width / height. Allow 5% extra for curve edge
			maskScaleX: projectHeaderSize.width / maskSizes.width,
			maskScaleY: projectHeaderSize.height / maskSizes.height
		};
	}

}
